import { useCallback, useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import type { TranscriptionMessage } from '@/hooks/useAgentTranscription'

interface TranscriptionDisplayProps {
  messages: TranscriptionMessage[]
  interimText: string
  className?: string
}

export function TranscriptionDisplay({
  messages,
  interimText,
  className,
}: TranscriptionDisplayProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isAtBottomRef = useRef(true)

  // Handle scroll - check if user is at/near bottom
  const handleScroll = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const threshold = 20
    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < threshold
    isAtBottomRef.current = isNearBottom
  }, [])

  // Only auto-scroll when user is at bottom (respects user scrolling up)
  useEffect(() => {
    if (isAtBottomRef.current && scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
  }, [messages, interimText])

  const hasContent = messages.length > 0 || interimText

  // Warm styled card container
  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className={cn(
        'rounded-xl bg-white/60 border border-amber-200/30 shadow-sm',
        'overflow-y-auto',
        hasContent ? 'px-4 py-3' : 'flex items-center justify-center',
        className
      )}
    >
      {!hasContent ? (
        <p className="text-sm text-stone-400 italic">
          Waiting for Kai to speak...
        </p>
      ) : (
        <div className="space-y-3">
          {messages.map((message) => (
            <p key={message.id} className="text-sm text-stone-700 leading-relaxed">
              {message.text}
            </p>
          ))}

          {interimText && (
            <p className="text-sm text-stone-400 leading-relaxed">
              {interimText}
              <span className="inline-flex ml-1">
                <span className="w-1 h-1 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1 h-1 bg-amber-400 rounded-full animate-bounce ml-0.5" style={{ animationDelay: '150ms' }} />
                <span className="w-1 h-1 bg-amber-400 rounded-full animate-bounce ml-0.5" style={{ animationDelay: '300ms' }} />
              </span>
            </p>
          )}

          {/* Scroll anchor */}
          <div ref={scrollRef} />
        </div>
      )}
    </div>
  )
}
